import React, { useCallback } from 'react';
import { useSnapshot } from 'valtio';
import Image from './image';

import '../../www/assets/css/color_bar.css';
import DataValueRect from '../../www/assets/images/data_value_rect.svg';
import ar from '../languages/ar';
import { datareadStore, datasetStore, videoStore, refsStore } from '../managers/globalState';
import ColorBarHtml, { PrecipitationColorBar } from './color_bar_html';

/**
 * Calculates the color reactangle style / positioning.
 * @param {number|null} percent
 * @param {boolean} isDoubleColorBar
 * @param {boolean} isUpper
 */
const setColorRectStyle = (percent, isDoubleColorBar = false, isUpper = true) => {
	const isValid = percent !== null;
	let colorRectStyle = null;

	if (isValid) {
		const colorBarPercent = isDoubleColorBar ? 60.9 : 71;
		const horizontalOffsetValue = isDoubleColorBar ? 25 : 13;
		const verticalOffsetValue = isDoubleColorBar ?
			(isUpper ? 15 : 76) :
			13;

		// percentage between 18% and 82% to fit actual color bar in color bar image.
		// Math.round(number * 100)/100 is used to trim the value to two decimal places.
    // * -1 to flip right to left
    //13 to 83
		const leftValue = horizontalOffsetValue + (Math.round(percent * colorBarPercent * 100) / 100) ;

		colorRectStyle = {
			display: 'table',
			position: 'absolute',
			left: `${leftValue.toString()}%`,
			top: `${verticalOffsetValue.toString()}px`
		};
	}

	// Set global store.
	datareadStore.setGlobalState({ colorRectStyle });
};

const ColorBarImage = ({ showDataRect = true }) => {
	// Get global state info.
	const { colorRectStyle, isCelcius } = useSnapshot(datareadStore.state);
	const { videoId } = useSnapshot(videoStore.state);
	const { get } = refsStore.stateSnapshot;

	const { vitalData } = get('content');
	const { dataset } = get('dataset');
	const videoManager = get('video');

	const videoInfo = videoManager.getVideo(videoId);

	// Define variables and color bar source.
	let temperature = vitalData?.value === 'air_temperature';

	const videoType = videoManager.getVideoType(videoId);

	let colorBarIndex = temperature && isCelcius ? 1 : 0;

	let colorBarSrc = dataset?.colorBarSrc?.[colorBarIndex] ?
		`https://climate.nasa.gov/assets/earth_now/arabic_colorbars/${dataset.colorBarSrc[colorBarIndex]}`
		: null;

	if (videoType === 'global' || videoType === 'geoLocated') {
		temperature = videoInfo.colorBarSrc?.includes('surfaceDayTemp_bar.png');
		colorBarIndex = temperature && isCelcius ? 1 : 0;
		colorBarSrc = videoInfo?.colorBarSrc?.[colorBarIndex] ?
			`https://climate.nasa.gov/assets/earth_now/arabic_colorbars/${videoInfo.colorBarSrc[temperature ? colorBarIndex : 0]}`
			: null;
	}

	const colorBarText = temperature ? ar['toggle_units'] : '';
	const clickable = temperature;

	const showDataReadRect = showDataRect && colorRectStyle && videoType !== 'global' && videoType !== 'geoLocated';

	const onClick = useCallback(() => {
		const newIsCelcius = !isCelcius;

		datareadStore.setGlobalState({ isCelcius: newIsCelcius });
	}, [isCelcius]);

	const colorBarShowing = (dataset || (videoType === 'global') || (videoType === 'geoLocated')) && colorBarSrc !== null && colorBarSrc !== undefined;
	datareadStore.setGlobalState({ isColorBarShowing: colorBarShowing });

	return colorBarShowing ?
		(
			<div className={`color-bar ${clickable ? 'clickable' : ''}`} onClick={clickable ? onClick : null}>
				{ showDataReadRect && <span style={colorRectStyle}><DataValueRect /></span> }
				<Image key={colorBarSrc} className='img-fluid no-drag' src={colorBarSrc} alt='Color bar' />
				{ colorBarText && <button className='toggle-units'>{colorBarText}</button> }
			</div>
		)
		: null;
};

/**
 * Decides whether to renderr the standard ColorBar (now named ColorBarImage), or the ColorBarHtml
 * Todo:
 * see if we can reduce the number of renders.
 * reduce prop drilling of showDataRect?
 *
 */
const ColorBar = props => {
	const { currentDataset } = useSnapshot(datasetStore.state);
	const { videoId } = useSnapshot(videoStore.state);
	const noVideo = videoId == null;

	if (!currentDataset) {
		return null;
	}

	// Unique component for precipitation color bar.
	if (currentDataset.externalId === 'precipitationToday' && noVideo) {
		return <PrecipitationColorBar {...props} />;
	}

	return currentDataset?.htmlColorBar ? <ColorBarHtml {...props} /> : <ColorBarImage {...props} />;
};

export default ColorBar;
export { setColorRectStyle };
